import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import {
  useCreateOfficialFee,
  useUploadOfficialFeeAttachments,
} from "../hooks/useOfficialFees";
import { isValidAccountNumber, formatAccountNumber } from "../utils/accountNumber";
import { extractApiError } from "../utils/apiError";
import { FEE_APPROVAL_THRESHOLD, isDescriptionRequired } from "../utils/feeThreshold";
import { OfficeAutocomplete } from "../components/OfficeAutocomplete";
import { SimilarFeesNotice } from "../components/SimilarFeesNotice";
import { officialFeesApi } from "../api/officialFees";
import type { FeeProject, OfficeSuggestion } from "../api/officialFees";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface Company {
  id: string;
  short_code: string;
  name: string;
}
interface Order {
  id: string;
  order_number: string;
  // Null dla podwykonawcy — API celowo nie zdradza mu, którego przedsięwzięcia
  // dotyczy jego zamówienie (OrderSerializer._projects_hidden).
  project_id: string | null;
  project_name: string | null;
  project_company_id: string | null;
  project_company_short_code: string | null;
  project_company_name: string | null;
  contractor_fee_requests_allowed: boolean;
}

// Not /projects/: that list stops at the projects a coordinator coordinates. This
// endpoint additionally honours the "Opłaty: wszystkie przedsięwzięcia" permission.
function useProjects() {
  return useQuery({
    queryKey: ["official-fee-available-projects"],
    queryFn: () => officialFeesApi.availableProjects(),
  });
}

function useCompanies() {
  return useQuery({
    queryKey: ["gf-company-choices"],
    queryFn: () =>
      apiClient
        .get<{ data: Company[] }>("/group-finance/company-choices/")
        .then((r) => r.data.data ?? []),
  });
}

function useContractorOrders() {
  return useQuery({
    queryKey: ["contractor-orders-for-fees"],
    queryFn: () =>
      apiClient
        .get<{ data: Order[] }>("/orders/")
        .then((r) =>
          (r.data.data ?? []).filter((o) => o.contractor_fee_requests_allowed)
        ),
  });
}

function useProjectCompanyMap(projects: FeeProject[]) {
  return useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of projects) map.set(p.id, p.company);
    return map;
  }, [projects]);
}

const baseSchema = {
  paying_company: z.string().min(1, "Wybierz spółkę płacącą"),
  office_name: z.string().min(2, "Podaj nazwę urzędu"),
  office_account_number: z
    .string()
    .min(1, "Podaj numer konta urzędu")
    .refine(isValidAccountNumber, "Numer konta musi zawierać 26 cyfr (opcjonalnie z przedrostkiem PL)"),
  office_address: z.string().min(5, "Podaj adres urzędu"),
  transfer_name: z.string().min(2, "Podaj tytuł przelewu"),
  amount: z
    .string()
    .min(1, "Podaj kwotę")
    .refine(
      (v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0,
      "Kwota musi być dodatnia",
    ),
  description: z.string().optional(),
  investor_name_required_in_title: z.boolean().default(false),
  is_power_of_attorney: z.boolean().default(false),
};

// Powyżej progu opis jest obowiązkowy — ta sama reguła co w serializerze backendu
// (OfficialFeeRequestCreateSerializer.validate), tu tylko po to, żeby użytkownik
// dowiedział się o niej przed wysłaniem formularza.
function requireDescriptionAboveThreshold(
  val: { amount: string; description?: string },
  ctx: z.RefinementCtx,
) {
  if (isDescriptionRequired(val.amount) && !val.description?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Opis jest obowiązkowy dla kwot powyżej ${FEE_APPROVAL_THRESHOLD} zł`,
      path: ["description"],
    });
  }
}

const internalSchema = z
  .object({
    project: z.string().min(1, "Wybierz przedsięwzięcie"),
    order: z.string().optional(),
    ...baseSchema,
  })
  .superRefine(requireDescriptionAboveThreshold);

// Bez `project` i z nieobowiązkową spółką płacącą: podwykonawca nie widzi
// przedsięwzięcia swojego zamówienia (API mu go nie zwraca), więc nie ma czego
// wpisać ani wybrać. Oba pola wyprowadza backend z wskazanego zamówienia.
// Wcześniej formularz wstawiał tu `null` z API i walidacja `z.string()` wywalała
// się na polu, którego nikt nie rysuje — przycisk „Złóż prośbę" nie robił nic.
const contractorSchema = z
  .object({
    order: z.string().min(1, "Wybierz zamówienie"),
    ...baseSchema,
    paying_company: z.string().optional(),
  })
  .superRefine(requireDescriptionAboveThreshold);

type InternalFormData = z.infer<typeof internalSchema>;
type ContractorFormData = z.infer<typeof contractorSchema>;
type FormData = InternalFormData | ContractorFormData;

// ── Internal user form ────────────────────────────────────────────────────────

function InternalForm({ prefill }: { prefill?: Partial<InternalFormData> }) {
  const navigate = useNavigate();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const createMut = useCreateOfficialFee();
  const uploadAttachmentsMut = useUploadOfficialFeeAttachments();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const projectCompanyMap = useProjectCompanyMap(projects);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InternalFormData>({
    resolver: zodResolver(internalSchema),
    defaultValues: prefill
      ? {
          project: prefill.project ?? "",
          order: prefill.order ?? "",
          paying_company: prefill.paying_company ?? "",
          office_name: prefill.office_name ?? "",
          office_account_number: prefill.office_account_number ?? "",
          office_address: prefill.office_address ?? "",
          transfer_name: prefill.transfer_name ?? "",
          amount: prefill.amount ?? "",
          description: prefill.description ?? "",
          investor_name_required_in_title: prefill.investor_name_required_in_title ?? false,
          is_power_of_attorney: prefill.is_power_of_attorney ?? false,
        }
      : {
          project: "",
          order: "",
          paying_company: "",
          office_name: "",
          office_account_number: "",
          office_address: "",
          transfer_name: "",
          amount: "",
          description: "",
          investor_name_required_in_title: false,
          is_power_of_attorney: false,
        },
  });

  const selectedProjectId = watch("project");

  // Auto-set paying_company when project changes.
  // Only selectedProjectId is a dep — projectCompanyMap and setValue are stable.
  useEffect(() => {
    if (!selectedProjectId) return;
    const company = projectCompanyMap.get(selectedProjectId);
    if (company) setValue("paying_company", company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const lockedCompanyId = selectedProject?.company ?? null;
  const lockedCompany = lockedCompanyId ? companies.find((c) => c.id === lockedCompanyId) : null;
  const companyLockedByProject = !!lockedCompanyId;

  const { onBlur: accountOnBlurRhf, ...accountReg } = register("office_account_number");
  function handleAccountBlur(e: React.FocusEvent<HTMLInputElement>) {
    const formatted = formatAccountNumber(e.target.value);
    e.target.value = formatted;
    accountOnBlurRhf(e);
    setValue("office_account_number", formatted, { shouldValidate: true });
  }

  const officeName = watch("office_name");

  function handleOfficeSuggestionSelect(s: OfficeSuggestion) {
    setValue("office_name", s.office_name, { shouldValidate: true });
    setValue("office_account_number", s.office_account_number, { shouldValidate: true });
    setValue("office_address", s.office_address, { shouldValidate: true });
  }

  async function onSubmit(data: InternalFormData) {
    setSubmitError(null);
    try {
      const created = await createMut.mutateAsync({
        project: data.project,
        order: data.order || undefined,
        paying_company: data.paying_company,
        office_name: data.office_name,
        office_account_number: data.office_account_number,
        office_address: data.office_address,
        transfer_name: data.transfer_name,
        amount: data.amount,
        description: data.description,
        investor_name_required_in_title: data.investor_name_required_in_title,
        is_power_of_attorney: data.is_power_of_attorney,
      });
      if (attachments.length) {
        await uploadAttachmentsMut.mutateAsync({ id: created.id, files: attachments });
      }
      navigate("/official-fees");
    } catch (err) {
      // Without this the rejection escapes handleSubmit as an unhandled promise and the
      // page just sits there — the refusal the API spelled out never reaches the screen.
      setSubmitError(extractApiError(err));
    }
  }

  if (projectsLoading || companiesLoading) {
    return <div className="p-6 text-[var(--color-text-secondary)]">Ładowanie...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Project */}
      <SearchableSelect
        label="Przedsięwzięcie *"
        placeholder="— wybierz —"
        options={projects.filter((p) => p.is_active).map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))}
        error={errors.project?.message}
        value={selectedProjectId}
        {...register("project")}
      />

      {/* Paying company */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Spółka płacąca <span className="text-red-500">*</span>
        </label>
        {companyLockedByProject ? (
          <>
            <input type="hidden" {...register("paying_company")} />
            <div className="input-base w-full bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] cursor-not-allowed">
              {lockedCompany
                ? `${lockedCompany.short_code} — ${lockedCompany.name}`
                : selectedProject?.company_name || "—"}
            </div>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">
              Ustawione automatycznie na podstawie przedsięwzięcia.
            </p>
          </>
        ) : (
          <SearchableSelect
            placeholder="— wybierz spółkę —"
            options={companies.map((c) => ({ value: c.id, label: `${c.short_code} — ${c.name}` }))}
            value={watch("paying_company")}
            {...register("paying_company")}
          />
        )}
        {errors.paying_company && (
          <p className="text-xs text-red-500 mt-1">{errors.paying_company.message}</p>
        )}
      </div>

      <SharedFields
        register={register}
        errors={errors}
        accountReg={accountReg}
        handleAccountBlur={handleAccountBlur}
        officeName={officeName}
        onOfficeNameChange={(v) => setValue("office_name", v, { shouldValidate: false })}
        onOfficeSuggestionSelect={handleOfficeSuggestionSelect}
        amount={watch("amount")}
      />

      <AttachmentsPicker
        files={attachments}
        setFiles={setAttachments}
        error={attachmentError}
        setError={setAttachmentError}
      />

      <SimilarFeesNotice
        officeName={watch("office_name")}
        officeAccountNumber={watch("office_account_number")}
        amount={watch("amount")}
        payingCompany={watch("paying_company")}
      />

      <FormActions
        isSubmitting={isSubmitting || createMut.isPending || uploadAttachmentsMut.isPending}
        submitError={submitError}
      />
    </form>
  );
}

// ── Contractor form ───────────────────────────────────────────────────────────

function ContractorForm({ prefill }: { prefill?: Partial<ContractorFormData> }) {
  const navigate = useNavigate();
  const { data: orders = [], isLoading: ordersLoading } = useContractorOrders();
  const createMut = useCreateOfficialFee();
  const uploadAttachmentsMut = useUploadOfficialFeeAttachments();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContractorFormData>({
    resolver: zodResolver(contractorSchema),
    defaultValues: prefill
      ? {
          order: prefill.order ?? "",
          paying_company: prefill.paying_company ?? "",
          office_name: prefill.office_name ?? "",
          office_account_number: prefill.office_account_number ?? "",
          office_address: prefill.office_address ?? "",
          transfer_name: prefill.transfer_name ?? "",
          amount: prefill.amount ?? "",
          description: prefill.description ?? "",
          investor_name_required_in_title: prefill.investor_name_required_in_title ?? false,
          is_power_of_attorney: prefill.is_power_of_attorney ?? false,
        }
      : {
          order: "",
          paying_company: "",
          office_name: "",
          office_account_number: "",
          office_address: "",
          transfer_name: "",
          amount: "",
          description: "",
          investor_name_required_in_title: false,
          is_power_of_attorney: false,
        },
  });

  const selectedOrderId = watch("order");
  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === selectedOrderId),
    [orders, selectedOrderId],
  );

  // Auto-set paying_company when order changes. Przedsięwzięcia tu nie ustawiamy —
  // API go podwykonawcy nie zwraca (project_id to null), a wpisanie tego nulla w
  // formularz było właśnie tym, co blokowało wysyłkę.
  useEffect(() => {
    if (!selectedOrder) return;
    if (selectedOrder.project_company_id) {
      setValue("paying_company", selectedOrder.project_company_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId]);

  const { onBlur: accountOnBlurRhf, ...accountReg } = register("office_account_number");
  function handleAccountBlur(e: React.FocusEvent<HTMLInputElement>) {
    const formatted = formatAccountNumber(e.target.value);
    e.target.value = formatted;
    accountOnBlurRhf(e);
    setValue("office_account_number", formatted, { shouldValidate: true });
  }

  const officeName = watch("office_name");

  function handleOfficeSuggestionSelect(s: OfficeSuggestion) {
    setValue("office_name", s.office_name, { shouldValidate: true });
    setValue("office_account_number", s.office_account_number, { shouldValidate: true });
    setValue("office_address", s.office_address, { shouldValidate: true });
  }

  async function onSubmit(data: ContractorFormData) {
    setSubmitError(null);
    try {
      const created = await createMut.mutateAsync({
        // Bez `project` — backend bierze je z zamówienia. Spółkę płacącą wysyłamy
        // tylko wtedy, gdy zamówienie ją pokazało; inaczej też wyprowadzi ją backend.
        order: data.order,
        paying_company: data.paying_company || undefined,
        office_name: data.office_name,
        office_account_number: data.office_account_number,
        office_address: data.office_address,
        transfer_name: data.transfer_name,
        amount: data.amount,
        description: data.description,
        investor_name_required_in_title: data.investor_name_required_in_title,
        is_power_of_attorney: data.is_power_of_attorney,
      });
      if (attachments.length) {
        await uploadAttachmentsMut.mutateAsync({ id: created.id, files: attachments });
      }
      navigate("/official-fees");
    } catch (err) {
      // Without this the rejection escapes handleSubmit as an unhandled promise and the
      // page just sits there — the refusal the API spelled out never reaches the screen.
      setSubmitError(extractApiError(err));
    }
  }

  if (ordersLoading) {
    return <div className="p-6 text-[var(--color-text-secondary)]">Ładowanie...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Order — required for contractors */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Zamówienie <span className="text-red-500">*</span>
        </label>
        {orders.length === 0 ? (
          <p className="text-sm text-amber-600 dark:text-amber-500 py-2">
            Żadne z Twoich zamówień nie zezwala na składanie prośb o opłatę urzędową.
            Uprawnienie nadaje umowa ramowa — skontaktuj się z opiekunem zamówienia,
            zanim wypełnisz formularz.
          </p>
        ) : (
          <SearchableSelect
            placeholder="— wybierz zamówienie —"
            // Sam numer zamówienia: nazwy przedsięwzięcia API podwykonawcy nie podaje,
            // więc doklejanie jej wypisywało na liście „NUMER — null".
            options={orders.map((o) => ({ value: o.id, label: o.order_number }))}
            value={watch("order")}
            {...register("order")}
          />
        )}
        {/* Outside the select on purpose: with no eligible order the select is not
            rendered at all, and an error nested inside it had nowhere to appear —
            "Złóż prośbę" failed validation and looked like a dead button. */}
        {errors.order && (
          <p className="text-xs text-red-500 mt-1">{errors.order.message}</p>
        )}
      </div>

      {/* Przedsięwzięcia tu nie ma i być nie może: portal podwykonawcy nie pokazuje,
          którego przedsięwzięcia dotyczy zamówienie. Wcześniej stał tu pusty kafelek
          „Przedsięwzięcie" — wyglądał jak pole do wypełnienia, którego nie da się
          wypełnić. Wniosek wiąże z przedsięwzięciem backend, po numerze zamówienia. */}

      {/* Paying company — read-only, auto-set from order's project */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Spółka płacąca <span className="text-red-500">*</span>
        </label>
        <input type="hidden" {...register("paying_company")} />
        {selectedOrder?.project_company_id ? (
          <>
            <div className="input-base w-full bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] cursor-not-allowed">
              {selectedOrder.project_company_short_code} — {selectedOrder.project_company_name}
            </div>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">
              Ustawione automatycznie na podstawie zamówienia.
            </p>
          </>
        ) : (
          <div className="input-base w-full bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]">
            {selectedOrder ? "Brak spółki przypisanej do przedsięwzięcia" : "— wybierz najpierw zamówienie —"}
          </div>
        )}
        {errors.paying_company && (
          <p className="text-xs text-red-500 mt-1">{errors.paying_company.message}</p>
        )}
      </div>

      <SharedFields
        register={register}
        errors={errors}
        accountReg={accountReg}
        handleAccountBlur={handleAccountBlur}
        officeName={officeName}
        onOfficeNameChange={(v) => setValue("office_name", v, { shouldValidate: false })}
        onOfficeSuggestionSelect={handleOfficeSuggestionSelect}
        amount={watch("amount")}
      />

      <AttachmentsPicker
        files={attachments}
        setFiles={setAttachments}
        error={attachmentError}
        setError={setAttachmentError}
      />

      {/* payingCompany falls back to "": it is optional in contractorSchema (the order
          may not expose a company), and "" is what useSimilarOfficialFees reads as "no
          company yet", which keeps the duplicate lookup disabled. */}
      <SimilarFeesNotice
        officeName={watch("office_name")}
        officeAccountNumber={watch("office_account_number")}
        amount={watch("amount")}
        payingCompany={watch("paying_company") ?? ""}
      />

      <FormActions
        isSubmitting={isSubmitting || createMut.isPending || uploadAttachmentsMut.isPending}
        submitError={submitError}
      />
    </form>
  );
}

// ── Attachments picker ────────────────────────────────────────────────────────

function AttachmentsPicker({
  files,
  setFiles,
  error,
  setError,
}: {
  files: File[];
  setFiles: (f: File[]) => void;
  error: string | null;
  setError: (e: string | null) => void;
}) {
  // Receives the FULL updated list (existing + added, or after removal).
  function handleFilesChange(next: File[]) {
    const oversize = next.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversize) {
      setError(`Plik "${oversize.name}" przekracza limit 25 MB.`);
      return;
    }
    setError(null);
    setFiles(next);
  }

  return (
    <div>
      <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
        Załączniki (opcjonalnie)
      </label>
      <p className="text-xs text-[var(--color-text-secondary)] mb-2">
        Pliki do przechowania razem z prośbą (np. wezwanie z urzędu, decyzja). Limit 25 MB na plik.
      </p>
      <FileDropZone files={files} onFilesChange={handleFilesChange} multiple />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Shared fields ─────────────────────────────────────────────────────────────

function SharedFields({
  register,
  errors,
  accountReg,
  handleAccountBlur,
  officeName,
  onOfficeNameChange,
  onOfficeSuggestionSelect,
  amount,
}: {
  register: any;
  errors: any;
  accountReg: any;
  handleAccountBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  officeName: string;
  onOfficeNameChange: (v: string) => void;
  onOfficeSuggestionSelect: (s: OfficeSuggestion) => void;
  amount: string;
}) {
  const { user } = useAuthStore();
  const aboveThreshold = isDescriptionRequired(amount);
  // Dyrektorowi prośba akceptuje się sama bez względu na kwotę, a pracownik i tak
  // czeka na akceptację przy każdej — próg zmienia coś tylko koordynatorowi.
  const needsDirector = user?.role === "coordinator";

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Nazwa urzędu <span className="text-red-500">*</span>
        </label>
        <OfficeAutocomplete
          value={officeName}
          onChange={onOfficeNameChange}
          onSelect={onOfficeSuggestionSelect}
          placeholder="np. Urząd Miasta Wrocław"
          hasError={!!errors.office_name}
        />
        {errors.office_name && (
          <p className="text-xs text-red-500 mt-1">{errors.office_name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Numer konta urzędu <span className="text-red-500">*</span>
        </label>
        <Input
          {...accountReg}
          onBlur={handleAccountBlur}
          placeholder="PL61 1090 1014 0000 0712 1981 2874"
          className="font-mono"
        />
        {errors.office_account_number && (
          <p className="text-xs text-red-500 mt-1">{errors.office_account_number.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Adres urzędu <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("office_address")}
          className="w-full rounded border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-bg-input)] text-[var(--color-text-primary)] min-h-[64px]"
          placeholder="ul. Szamocka 3, 01-748 Warszawa"
        />
        {errors.office_address && (
          <p className="text-xs text-red-500 mt-1">{errors.office_address.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Tytuł przelewu <span className="text-red-500">*</span>
        </label>
        <Input {...register("transfer_name")} placeholder="np. Opłata za pełnomocnictwo" />
        {errors.transfer_name && (
          <p className="text-xs text-red-500 mt-1">{errors.transfer_name.message}</p>
        )}
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            {...register("investor_name_required_in_title")}
            className="rounded border-[var(--color-border)] accent-[var(--color-primary)] w-4 h-4"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            Wymagana nazwa inwestora w tytule
          </span>
        </label>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            {...register("is_power_of_attorney")}
            className="rounded border-[var(--color-border)] accent-[var(--color-primary)] w-4 h-4"
          />
          <span className="text-sm text-[var(--color-text-primary)]">
            Opłata za pełnomocnictwo
          </span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          Kwota (PLN) <span className="text-red-500">*</span>
        </label>
        <Input
          {...register("amount")}
          type="number"
          step="0.01"
          min="0.01"
          placeholder="250.00"
          className="font-mono [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {errors.amount && (
          <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>
        )}
        {aboveThreshold && needsDirector && (
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            Powyżej {FEE_APPROVAL_THRESHOLD} zł prośbę akceptuje dyrektor — sam(a)
            zatwierdzasz opłaty do {FEE_APPROVAL_THRESHOLD} zł włącznie.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
          {aboveThreshold ? (
            <>
              Opis <span className="text-red-500">*</span>
            </>
          ) : (
            "Opis (opcjonalny)"
          )}
        </label>
        <textarea
          {...register("description")}
          className="w-full rounded border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-bg-input)] text-[var(--color-text-primary)] min-h-[64px]"
          placeholder={
            aboveThreshold
              ? `Za co jest ta opłata? (wymagane powyżej ${FEE_APPROVAL_THRESHOLD} zł)`
              : "Dodatkowe informacje..."
          }
        />
        {errors.description && (
          <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>
        )}
      </div>
    </>
  );
}

function FormActions({
  isSubmitting,
  submitError,
}: {
  isSubmitting: boolean;
  submitError: string | null;
}) {
  const navigate = useNavigate();
  return (
    <div className="pt-2">
      {submitError && (
        <p
          role="alert"
          className="mb-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400"
        >
          {submitError}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={() => navigate("/official-fees")}>
          Anuluj
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Wysyłanie..." : "Złóż prośbę"}
        </Button>
      </div>
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

export default function OfficialFeeRequestCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const location = useLocation();
  const prefill = (location.state as any)?.prefill as Partial<FormData> | undefined;

  const isContractor = user?.role === "contractor";

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/official-fees")}
        className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] mb-4"
      >
        <ArrowLeft size={14} />
        Wróć do listy
      </button>

      <h1 className="text-xl font-semibold text-[var(--color-text-primary)] mb-6">
        {prefill ? "Nowa prośba (kopiowanie)" : "Nowa prośba o opłatę urzędową"}
      </h1>

      {isContractor ? (
        <ContractorForm prefill={prefill as Partial<ContractorFormData>} />
      ) : (
        <InternalForm prefill={prefill as Partial<InternalFormData>} />
      )}
    </div>
  );
}
